import React from 'react';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import { useBreakpoint } from 'hooks';
import { Text, Tabs, TabPane, ListingTable } from 'components';
import { featureEnabled } from 'utils';
import { Color } from '@bb/elements-web-rebranding';
import { StyledPaneTitle } from './StyledComponents';
import MyBrokerageContext from './MyBrokerageContext';
import OpenHouses from './OpenHouses';
import HavesWants from './HavesWants';

type KeyType = 'listings' | 'openHouses' | 'havesWants';

const MyBrokerageTabs: React.FC = () => {
  const {
    queryData,
    setQueryData,
    initialQueryData,
    selectedRowKeys,
    showOpenHouses,
    results,
    loading,
    setRedirect,
    sort,
    heightOffset,
    setSelectedRowKeys,
  } = React.useContext(MyBrokerageContext);

  const { md } = useBreakpoint();

  const openDisabledOpenHousesModal = () => {
    Swal.fire({
      title: 'Open Houses',
      html: `
        Any and all advertising of open houses on BrokerBay have been suspended by your brokerage.
        <br /><br />
        If you have any questions, please contact your Brokerage Administrator.
        `,
      type: 'warning',
      cancelButtonText: 'Close',
      showCancelButton: true,
      showConfirmButton: false,
    });
  };

  const handleTabChange = (key: KeyType) => {
    if (key === 'openHouses' && !showOpenHouses) {
      return openDisabledOpenHousesModal();
    }
    setQueryData(() => ({ ...cloneDeep(initialQueryData), type: key }));
  };

  const renderTab = (key: KeyType) => {
    const getTitle = () => {
      if (key === 'listings') return 'Listings';
      if (key === 'openHouses') return 'Open Houses';
      return 'Haves / Wants';
    };
    const getContent = () => {
      if (key === 'listings')
        return (
          <ListingTable
            listings={results.listings}
            loading={loading}
            setRedirect={setRedirect}
            sort={sort}
            sortField={queryData.sortData.field}
            heightOffset={heightOffset}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        );
      if (key === 'openHouses') return <OpenHouses />;
      return <HavesWants />;
    };

    return (
      <TabPane
        tab={
          <StyledPaneTitle textAlign="center" $active={key === queryData.type}>
            {getTitle()}
          </StyledPaneTitle>
        }
        key={key}
      >
        {getContent()}
      </TabPane>
    );
  };

  const TabsComponent = featureEnabled('supraRebrandingFeature', 1)
    ? StyledTabs
    : Tabs;

  return (
    <TabsComponent
      type="primary"
      centered={!md}
      activeKey={queryData.type}
      onTabClick={handleTabChange}
      tabBarExtraContent={
        md && (
          <div>
            {/* Temporarily disabled for [SUPRANN-161] */}
            {/* {selectedRowKeys.length > 0 && (
              <StyledSendLink to={`/listing/send/${selectedRowKeys.join(',')}`}>
                Send {selectedRowKeys.length}{' '}
                {selectedRowKeys.length === 1 ? 'Listing' : 'Listings'}
              </StyledSendLink>
            )} */}
            {results.count > 0 && (
              <Text type="secondary">
                {results.count} {results.count === 1 ? 'Result' : 'Results'}
              </Text>
            )}
          </div>
        )
      }
    >
      {renderTab('listings')}
      {renderTab('openHouses')}
      {renderTab('havesWants')}
    </TabsComponent>
  );
};

export default MyBrokerageTabs;

const StyledTabs = styled(Tabs)`
  .ant-tabs-ink-bar {
    background-color: ${Color.PRIMARY};
  }
`;
